import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/context/AuthProvider";
import { useCompanyContext } from "../../common/company/CompanyContext";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import CompanyIdentityHeader from "../../common/company/CompanyIdentityHeader";
import DeclrChartOfAccountsController, { TARGET_FIELDS } from "./DeclrChartOfAccountsController";

const emptyNewRow = { SourceCode: "", Description: "", CategoryTag: "" };

// XbrlTag is stored as "{namespace-url}LocalCode" - split it so the
// human-readable measure name can lead, then the short code, then the url.
const splitXbrlTag = (tag) => {
    if (!tag) return { code: "", url: "" };
    const match = /^\{(.+)\}(.+)$/.exec(tag);
    return match ? { url: match[1], code: match[2] } : { code: tag, url: "" };
};

// Visible text is the human-readable measure name only; the machine
// code/namespace url are available as a mouseover tooltip, not inline.
const conceptDisplayText = (measureName, xbrlTag) => {
    if (measureName) return measureName;
    const { code } = splitXbrlTag(xbrlTag);
    return code;
};

const conceptTooltip = (xbrlTag) => {
    const { code, url } = splitXbrlTag(xbrlTag);
    return [code, url].filter(Boolean).join(" — ");
};

/**
 * 🎯 COMPANY CHART OF ACCOUNTS
 * The persistent, per-company dataset of GL/cost codes a rep manages
 * directly - add/edit/remove rows by hand, or bulk-import a CSV (parsed
 * entirely in the browser - no S3, no file upload). A row is unusable in a
 * declaration until mapped to a taxonomy scope; that's a visible status,
 * not an error. Publish snapshots every mapped row into an immutable
 * version - that snapshot, not this working dataset, is what a
 * declaration actually reads.
 */
export default function DeclrChartOfAccountsView() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { activeCompanyId, activeCompanyName } = useCompanyContext();
    const siteKey = process.env.REACT_APP_SITE_KEY;
    const SITE_NAME = process.env.REACT_APP_SITE_NAME || "sustina.nrgpix.ch";
    const fileInputRef = useRef(null);

    const {
        rows,
        concepts,
        definitions,
        publishedVersions,
        loading,
        error,
        mappedCount,
        file,
        headers,
        sampleRows,
        columnMapping,
        parsing,
        importing,
        isSourceCodeMapped,
        handleFileSelect,
        handleMappingChange,
        handleImport,
        cancelUpload,
        handleAddRow,
        handleUpdateRow,
        handleRemoveRow,
        handleSetMapping,
        handleClearMapping,
        publishing,
        handlePublish,
    } = DeclrChartOfAccountsController(activeCompanyId, siteKey);

    const [newRow, setNewRow] = useState(emptyNewRow);
    const [mappingRowId, setMappingRowId] = useState(null);
    const [mappingForm, setMappingForm] = useState({ attributeDefinitionId: "", newAttributeLabel: "", taxonomyDetailRowId: "" });

    const handleSignOut = async () => {
        await logout();
        navigate("/auth/sign-in");
    };

    if (!activeCompanyId) {
        navigate("/registered/dashboard");
        return null;
    }

    const openMapping = (row) => {
        const rowId = GoldenSourceHelper.get(row, "ChartOfAccountsRowId");
        setMappingRowId(rowId);
        setMappingForm({
            attributeDefinitionId: GoldenSourceHelper.get(row, "AttributeDefinitionId") || "",
            newAttributeLabel: "",
            taxonomyDetailRowId: GoldenSourceHelper.get(row, "TaxonomyDetailRowId") || "",
        });
    };

    const saveMapping = async () => {
        if (!mappingForm.taxonomyDetailRowId || (!mappingForm.attributeDefinitionId && !mappingForm.newAttributeLabel)) return;
        await handleSetMapping({
            ChartOfAccountsRowId: mappingRowId,
            AttributeDefinitionId: mappingForm.attributeDefinitionId || null,
            NewAttributeLabel: mappingForm.attributeDefinitionId ? null : mappingForm.newAttributeLabel,
            TaxonomyDetailRowId: mappingForm.taxonomyDetailRowId,
        });
        setMappingRowId(null);
    };

    return (
        <main id="main" className="page-main" data-scroll-container>
            <div className="page-main-inner">
                <section className="page-section">
                    <div className="container mt-4">
                        <div className="row mb-2">
                            <div className="col">
                                <h2 className="h4 fw-bold mb-1">Chart of Accounts</h2>
                                <p className="text-muted small mb-0">
                                    Your GL/cost code dataset — upload or manage by hand, map each line to an ESG
                                    scope, then publish the mapped set for use in a declaration.
                                </p>
                            </div>
                        </div>

                        <CompanyIdentityHeader companyId={activeCompanyId} siteKey={siteKey} />

                        {error && <div className="alert alert-danger extra-small">{error}</div>}

                        {!loading && concepts.length === 0 && (
                            <div className="alert alert-warning extra-small d-flex justify-content-between align-items-center">
                                <span>No ESG scope selected yet — mapping needs at least one concept chosen at Company ESG Scope.</span>
                                <button className="btn btn-sm btn-outline-dark fw-bold" onClick={() => navigate("/registered/company-scope")}>
                                    Go to Company ESG Scope
                                </button>
                            </div>
                        )}

                        {/* UPLOAD */}
                        <div className="card shadow-sm border-0 bg-white p-3 mb-3">
                            <h6 className="extra-small fw-bold text-uppercase text-primary mb-3">Upload File</h6>
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept=".csv"
                                className="d-none"
                                onChange={(e) => handleFileSelect(e.target.files[0])}
                            />
                            {!file && (
                                <button
                                    className="btn btn-sm btn-outline-secondary align-self-start"
                                    onClick={() => fileInputRef.current.click()}
                                    disabled={parsing}
                                >
                                    {parsing ? "Parsing..." : "Browse CSV File..."}
                                </button>
                            )}

                            {file && headers.length > 0 && (
                                <>
                                    <div className="extra-small text-muted mb-2">{file.name}</div>
                                    <div className="table-responsive mb-2">
                                        <table className="table table-sm align-middle">
                                            <thead>
                                                <tr>
                                                    <th className="extra-small">Column</th>
                                                    <th className="extra-small">Sample</th>
                                                    <th className="extra-small">Maps To</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {headers.map((h, i) => (
                                                    <tr key={i}>
                                                        <td className="extra-small fw-bold">{h}</td>
                                                        <td className="extra-small text-muted">{sampleRows[0] ? sampleRows[0][i] : ""}</td>
                                                        <td>
                                                            <select
                                                                className="form-select form-select-sm"
                                                                value={columnMapping[i] || ""}
                                                                onChange={(e) => handleMappingChange(i, e.target.value)}
                                                            >
                                                                <option value="">-- ignore --</option>
                                                                {TARGET_FIELDS.map((f) => (
                                                                    <option key={f.key} value={f.key}>{f.label}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {!isSourceCodeMapped && (
                                        <div className="extra-small text-danger mb-2">
                                            Map at least one column to "GL / Source Code" before importing.
                                        </div>
                                    )}
                                    <div className="d-flex gap-2">
                                        <button
                                            className="btn btn-sm btn-primary fw-bold"
                                            disabled={!isSourceCodeMapped || importing}
                                            onClick={handleImport}
                                        >
                                            {importing ? "IMPORTING..." : "IMPORT INTO DATASET"}
                                        </button>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={cancelUpload} disabled={importing}>
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* DATASET */}
                        <div className="card shadow-sm border-0 bg-white overflow-hidden mb-3">
                            <div className="p-3 border-bottom bg-light d-flex justify-content-between align-items-center">
                                <h6 className="extra-small fw-bold text-uppercase text-primary mb-0">
                                    Dataset ({rows.length} rows, {mappedCount} mapped)
                                </h6>
                                <button className="btn btn-sm btn-success fw-bold" disabled={publishing || mappedCount === 0} onClick={handlePublish}>
                                    {publishing ? "PUBLISHING..." : "PUBLISH"}
                                </button>
                            </div>

                            {/* Manual add row */}
                            <div className="p-3 border-bottom d-flex gap-2 align-items-end flex-wrap">
                                <div>
                                    <label className="extra-small fw-bold text-muted d-block mb-1">GL Code</label>
                                    <input className="form-control form-control-sm" style={{ width: 140 }}
                                        value={newRow.SourceCode} onChange={(e) => setNewRow((p) => ({ ...p, SourceCode: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="extra-small fw-bold text-muted d-block mb-1">Description</label>
                                    <input className="form-control form-control-sm" style={{ width: 260 }}
                                        value={newRow.Description} onChange={(e) => setNewRow((p) => ({ ...p, Description: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="extra-small fw-bold text-muted d-block mb-1">Category</label>
                                    <input className="form-control form-control-sm" style={{ width: 160 }}
                                        value={newRow.CategoryTag} onChange={(e) => setNewRow((p) => ({ ...p, CategoryTag: e.target.value }))} />
                                </div>
                                <button
                                    className="btn btn-sm btn-outline-primary fw-bold"
                                    disabled={!newRow.SourceCode.trim()}
                                    onClick={async () => { await handleAddRow(newRow); setNewRow(emptyNewRow); }}
                                >
                                    Add Row
                                </button>
                            </div>

                            <div className="table-responsive" style={{ maxHeight: 560 }}>
                                <table className="table table-hover table-sm mb-0 align-middle">
                                    <tbody>
                                        {loading && <tr><td className="p-4 text-center text-muted extra-small italic">Loading...</td></tr>}
                                        {!loading && rows.length === 0 && (
                                            <tr><td className="p-4 text-center text-muted extra-small italic">No rows yet — upload a file or add one above.</td></tr>
                                        )}
                                        {rows.map((r, i) => {
                                            const rowId = GoldenSourceHelper.get(r, "ChartOfAccountsRowId");
                                            const status = GoldenSourceHelper.get(r, "MappingStatusCode");
                                            const isMapping = mappingRowId === rowId;
                                            return (
                                                <React.Fragment key={rowId || i}>
                                                    <tr>
                                                        <td className="ps-3">
                                                            <div className="fw-bold extra-small">
                                                                {GoldenSourceHelper.get(r, "SourceCode")} — {GoldenSourceHelper.get(r, "Description")}
                                                            </div>
                                                            <div className="extra-small text-muted">
                                                                {GoldenSourceHelper.get(r, "CategoryTag")}
                                                                {status === "MAPPED" && (
                                                                    <> &middot; {GoldenSourceHelper.get(r, "AttributeLabel")} <i className="bi bi-arrow-right mx-1"></i>
                                                                    <span title={conceptTooltip(GoldenSourceHelper.get(r, "XbrlTag"))}>
                                                                        {conceptDisplayText(GoldenSourceHelper.get(r, "MeasureName"), GoldenSourceHelper.get(r, "XbrlTag"))}
                                                                    </span></>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={{ width: 100 }}>
                                                            <span className={`badge ${status === "MAPPED" ? "bg-success" : "bg-warning text-dark"}`}>
                                                                {status}
                                                            </span>
                                                        </td>
                                                        <td className="text-end pe-3" style={{ width: 220 }}>
                                                            {status === "MAPPED" ? (
                                                                <button className="btn btn-sm btn-outline-secondary fw-bold me-1" onClick={() => handleClearMapping(rowId)}>
                                                                    Unmap
                                                                </button>
                                                            ) : (
                                                                <button className="btn btn-sm btn-outline-primary fw-bold me-1" onClick={() => openMapping(r)}>
                                                                    Map
                                                                </button>
                                                            )}
                                                            <button className="btn btn-sm btn-outline-danger fw-bold" onClick={() => handleRemoveRow(rowId)}>
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {isMapping && (
                                                        <tr>
                                                            <td colSpan={3} className="bg-light p-3">
                                                                <div className="d-flex gap-2 flex-wrap align-items-end">
                                                                    <div>
                                                                        <label className="extra-small fw-bold text-muted d-block mb-1">Existing Attribute</label>
                                                                        <select className="form-select form-select-sm" style={{ width: 220 }}
                                                                            value={mappingForm.attributeDefinitionId}
                                                                            onChange={(e) => setMappingForm((p) => ({ ...p, attributeDefinitionId: e.target.value }))}>
                                                                            <option value="">-- or create new --</option>
                                                                            {definitions.map((d) => {
                                                                                const id = GoldenSourceHelper.get(d, "AttributeDefinitionId");
                                                                                return <option key={id} value={id}>{GoldenSourceHelper.get(d, "Label")}</option>;
                                                                            })}
                                                                        </select>
                                                                    </div>
                                                                    {!mappingForm.attributeDefinitionId && (
                                                                        <div>
                                                                            <label className="extra-small fw-bold text-muted d-block mb-1">New Attribute Label</label>
                                                                            <input className="form-control form-control-sm" style={{ width: 220 }}
                                                                                value={mappingForm.newAttributeLabel}
                                                                                onChange={(e) => setMappingForm((p) => ({ ...p, newAttributeLabel: e.target.value }))} />
                                                                        </div>
                                                                    )}
                                                                    <div>
                                                                        <label className="extra-small fw-bold text-muted d-block mb-1">Taxonomy Concept</label>
                                                                        <select className="form-select form-select-sm" style={{ width: 280 }}
                                                                            value={mappingForm.taxonomyDetailRowId}
                                                                            onChange={(e) => setMappingForm((p) => ({ ...p, taxonomyDetailRowId: e.target.value }))}>
                                                                            <option value="">-- select --</option>
                                                                            {concepts.map((c) => {
                                                                                const cId = GoldenSourceHelper.get(c, "TaxonomyDetailRowId");
                                                                                const xbrlTag = GoldenSourceHelper.get(c, "XbrlTag");
                                                                                const measureName = GoldenSourceHelper.get(c, "MeasureName");
                                                                                return (
                                                                                    <option key={cId} value={cId} title={conceptTooltip(xbrlTag)}>
                                                                                        {conceptDisplayText(measureName, xbrlTag)}
                                                                                    </option>
                                                                                );
                                                                            })}
                                                                        </select>
                                                                    </div>
                                                                    <button className="btn btn-sm btn-primary fw-bold" onClick={saveMapping}>Save</button>
                                                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setMappingRowId(null)}>Cancel</button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* PUBLISHED VERSIONS */}
                        {publishedVersions.length > 0 && (
                            <div className="card shadow-sm border-0 bg-white p-3 mb-3">
                                <h6 className="extra-small fw-bold text-uppercase text-primary mb-3">Published Versions</h6>
                                <table className="table table-sm align-middle mb-0">
                                    <tbody>
                                        {publishedVersions.map((v, i) => (
                                            <tr key={GoldenSourceHelper.get(v, "VersionId") || i}>
                                                <td className="extra-small fw-bold">v{GoldenSourceHelper.get(v, "VersionNumber")}</td>
                                                <td className="extra-small text-muted">{GoldenSourceHelper.get(v, "PublishedAtUtc")}</td>
                                                <td className="extra-small text-muted">{GoldenSourceHelper.get(v, "MappedLineCount")} rows</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* FOOTER NAVIGATION */}
                        <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
                            <div className="d-flex gap-3">
                                <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={() => navigate("/registered/company-dashboard")}>
                                    <i className="bi bi-arrow-left me-2"></i> Back to Dashboard
                                </button>
                                <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={handleSignOut}>
                                    <i className="bi bi-box-arrow-left me-2"></i> Sign Out
                                </button>
                            </div>
                            <div className="text-end">
                                <span className="text-muted extra-small d-block">Workflow Context</span>
                                <span className="fw-bold small">{activeCompanyName || SITE_NAME}</span>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </main>
    );
}
